import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { userSelectedLocationInfoSelector } from '../../../../store/user/user.selectors';
import PropTypes from 'prop-types';
import { setUserAgencyIdOperation } from '../../../../store/user/user.operations';
import {
    getAgencyRoutesOperation,
    getRoutesStopsOperation,
} from '../../../../routes/routes/routes.operations';
import { userAgenciesSelector } from '../../../../store/user-info/user-info.selectors';
import { onAgencyChange } from '../../../../services/network/agencyAbortController';
import LytDropdown from '../../../library/dropdown/LytDropdown';
import { ICON_NAMES } from '../../../library/icon/icon-constants';
const SelectorContainer = styled.div`
    padding: 8px 16px;
    position: relative;
`;
const DropdownWrapper = styled.div`
    width: 100%;
`;
const CollapsedContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px 0;
`;

/**
 * @typedef {Object} AgencySelectorProps
 * @property {boolean} collapsed - Whether the agency selector is collapsed
 */

/**
 * @param {AgencySelectorProps} props
 * @returns {React.ReactNode}
 */
const LytAgencySelector = ({ collapsed = false }) => {
    const dispatch = useDispatch();
    const { list, loading } = useSelector(userAgenciesSelector);
    const { agencyId } = useSelector(userSelectedLocationInfoSelector);
    useEffect(() => {
        if (agencyId) {
            // Abort all pending requests for previous agency
            onAgencyChange();

            // Make requests for new agency (they'll use the new signal)
            dispatch(getAgencyRoutesOperation());
            dispatch(getRoutesStopsOperation({}));
        }
    }, [agencyId, dispatch]);
    const handleAgencySelect = useCallback(
        (newAgencyId) => {
            const newAgency = list.find(
                (agency) => agency.agencies_id === newAgencyId
            );
            if (newAgency && newAgency.agencies_id !== agencyId) {
                dispatch(setUserAgencyIdOperation(newAgency.agencies_id));
            }
        },
        [dispatch, list, agencyId]
    );
    const dropdownData = useMemo(
        () =>
            list
                .map(({ agencies_id, agencies_short_name }) => ({
                    id: agencies_id,
                    text: agencies_short_name,
                }))
                .sort((a, b) => a.text.localeCompare(b.text)),
        [list]
    );
    const selectedAgencyName = useMemo(() => {
        if (loading) return 'Loading...';
        const agency = list.find((a) => a.agencies_id === agencyId);
        return agency?.agencies_short_name || 'Select Agency';
    }, [list, agencyId, loading]);

    // Handle dropdown change from LytDropdown
    const handleDropdownChange = useCallback(
        (selectedOptions) => {
            const selectedId = Object.keys(selectedOptions).find(
                (key) => selectedOptions[key]
            );
            if (selectedId) {
                handleAgencySelect(selectedId);
            }
        },
        [handleAgencySelect]
    );

    // Don't render if there are no agencies
    if (!loading && list.length === 0) {
        return null;
    }
    if (collapsed) {
        return (
            <CollapsedContainer>
                <LytDropdown
                    options={dropdownData}
                    selectedOptions={{
                        [agencyId]: true,
                    }}
                    onChange={handleDropdownChange}
                    dropdownPlaceholder={selectedAgencyName}
                    loading={loading}
                    searchable
                    searchPlaceholder="Search agencies..."
                    triggerIcon={ICON_NAMES.VerticalDots}
                    triggerType="secondary"
                    usePortal
                />
            </CollapsedContainer>
        );
    }

    // Expanded state - use LytDropdown
    return (
        <SelectorContainer>
            <DropdownWrapper>
                <LytDropdown
                    options={dropdownData}
                    selectedOptions={{
                        [agencyId]: true,
                    }}
                    onChange={handleDropdownChange}
                    dropdownPlaceholder={selectedAgencyName}
                    loading={loading}
                    searchable
                    searchPlaceholder="Search agencies..."
                    fullWidth
                />
            </DropdownWrapper>
        </SelectorContainer>
    );
};
LytAgencySelector.propTypes = {
    collapsed: PropTypes.bool,
};
export default LytAgencySelector;
